import { Column } from 'primereact/column'
import {
  DataTable,
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableRowExpansionTemplate,
  SortOrder
} from 'primereact/datatable'
import { FC, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { RunStatus, WorkflowSubmission } from '../../types/types'
import { CollapseIcon, CompareIcon, ExpandIcon, PlayIcon } from '../Icons'
import { computeSupersededIds } from '../../types/submission-util'
import IconButton from '../buttons/IconButton'
import SubmissionStatusCell from './SubmissionStatusCell'
import { InputText } from 'primereact/inputtext'
import { useCommitRun, useGetSubmission } from '../../api/submission-API'
import toast from 'react-hot-toast'
import { Button } from 'primereact/button'

const filterComp = options => {
  return (
    <div className="px-1 py-2">
      <InputText
        value={options.value}
        onChange={e => {
          console.log('FilterElement', options)
          options.filterApplyCallback(e.target.value)
        }}
        className="w-full"
      />
    </div>
  )
}

type SubmissionListProp = {
  loading?: boolean
  onPageChange: (event: DataTablePageEvent) => void
  submissions: WorkflowSubmission[]
}

const SubmissionList: FC<SubmissionListProp> = ({
  submissions,
  loading,
  onPageChange
}: SubmissionListProp) => {
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({})
  const [openPriorSections, setOpenPriorSections] = useState<Record<string, boolean>>({})
  const [runList, setRunList] = useState([] as WorkflowSubmission[])
  const [sortField, setSortField] = useState<string>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1)

  const [activeRunId, setActiveRunId] = useState<string>('')
  const [idForPeriview, setIdForPeriview] = useState<string>()
  const [{ loading: committingSubmission }, commitSubmission] = useCommitRun()
  const [{ loading: loadingRun }, loadRun] = useGetSubmission()

  // Compute overall status from active (non-superseded) cases only
  const verifyOverallStatus = (submission: WorkflowSubmission): RunStatus => {
    // Priority: ERROR > RUNNING > PENDING > SUBMITTED > CREATED > WAITING > UNSPECIFIED > COMPLETED
    const statusPriority: Record<number, number> = {
      [RunStatus.ERROR]: 8,
      [RunStatus.RUNNING]: 7,
      [RunStatus.PENDING]: 6,
      [RunStatus.SUBMITTED]: 5,
      [RunStatus.WAITING]: 4,
      [RunStatus.UNSPECIFIED]: 3,
      [RunStatus.WARN_ON_ERROR]: 2,
      [RunStatus.COMPLETED]: 1,
      [RunStatus.CANCELLED]: 1,
      [RunStatus.CREATED]: 0
    }
    let highestPriority = -1
    let highestStatus: RunStatus = RunStatus.UNSPECIFIED

    submission.submissionRunGroups?.forEach((srg: any) => {
      const latestByName: Record<string, string> = {}
      srg.workflowRunGroups?.forEach((wrg: any) => {
        if (wrg.name !== 'All') {
          latestByName[wrg.name] = wrg.systemRunId
        }
      })

      srg.workflowRunGroups?.forEach((wrg: any) => {
        if (wrg.name !== 'All' && wrg.systemRunId !== latestByName[wrg.name]) return

        wrg.workflow?.forEach((wf: any) => {
          const st = wf.overallStatus ?? RunStatus.UNSPECIFIED
          const pri = statusPriority[st] ?? 0
          if (pri > highestPriority) {
            highestPriority = pri
            highestStatus = st
          }
        })
      })
    })

    return highestStatus === RunStatus.UNSPECIFIED ? RunStatus.CREATED : highestStatus
  }

  const columns = [
    {
      header: 'ID',
      field: 'shortName',
      sortable: true,
      headerClassName: 'w-[120px]',
      bodyClassName: 'w-[120px] id-cell',
      filter: true,
      filterElement: filterComp,
      filterFunction: (value, filter) => {
        console.log('filterFunction', value, filter)
        return value && value.toString().includes(filter)
      },
      body: rowData => {
        const shortName = rowData.shortName
        const rowName = rowData.name
        return (
          <div className="flex items-center justify-between" id="case-name">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => {
                // get current row id => if in expandRows, remove; else, append
                const copyExpandedRows: DataTableExpandedRows = { ...expandedRows }
                if (expandedRows[rowName]) {
                  // if rowName exists in expandedRows
                  // delete rowName from expandedRows
                  delete copyExpandedRows[rowName]
                } else {
                  // if rowName doesnt exist, add rowName to expandedRows
                  copyExpandedRows[rowName] = true
                }
                setExpandedRows(copyExpandedRows)
              }}>
              {expandedRows[rowName] ? (
                <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              ) : (
                <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              )}
              <Link to={`/submissions/${rowData.name}`}>{shortName}</Link>
            </div>
          </div>
        )
      }
    },
    {
      header: 'Description',
      field: 'runConfig.description'
    },
    {
      header: 'Reference Run',
      field: 'runConfig.referenceRunName',
      filter: true,
      sortable: true,
      headerClassName: 'w-[150px]'
    },
    {
      header: 'Master Geometry',
      field: 'runConfig.baselineRunName',
      filter: true,
      sortable: true,
      headerClassName: 'w-[150px]'
    },
    {
      header: 'Overall Status',
      field: 'overallstatus',
      sortable: true,
      headerClassName: 'w-[200px]',
      body: (rowData: WorkflowSubmission) => {
        return <SubmissionStatusCell status={verifyOverallStatus(rowData)} />
      }
    }
  ]

  useEffect(() => {
    setRunList([...submissions])
  }, [submissions])

  const handleSubmitRun = async (runId: string) => {
    if (committingSubmission) {
      return
    }
    try {
      // commit submission, start run
      setActiveRunId(runId)
      await commitSubmission(runId)
      toast.success('Workflow submitted.')
      setActiveRunId('')
      setRunList(
        runList.map(item => {
          if (item._id === runId) {
            return {
              ...item,
              overallStatus: RunStatus.SUBMITTED
            } as WorkflowSubmission
          }
          return item
        })
      )
    } catch (err) {
      console.error(err)
      toast.error('Error submitting workflow.')
    }
  }

  return (
    <>
      <DataTable
        value={runList}
        dataKey="name"
        filterDisplay="row"
        loading={loading}
        expandedRows={expandedRows}
        rowExpansionTemplate={(
          rowData: WorkflowSubmission,
          options: DataTableRowExpansionTemplate
        ) => {
          // Compute superseding across ALL containers so a per-case resubmit (appended as a
          // separate container) retires the run it replaced. Keys drafting positions by the
          // following car's stable localPosition via getSupersedeCaseKey.
          const supersededIds = computeSupersededIds(
            rowData.submissionRunGroups.flatMap(c => c.workflowRunGroups)
          )
          return (
            <section className="max-h-80 overflow-auto">
              {rowData.submissionRunGroups.map(runGroup => {
                const activeCases =
                  runGroup?.workflowRunGroups.filter(
                    wrg => wrg.name !== 'All' && !supersededIds.has(wrg.systemRunId)
                  ) || []
                const priorCases =
                  runGroup?.workflowRunGroups.filter(
                    wrg => wrg.name !== 'All' && supersededIds.has(wrg.systemRunId)
                  ) || []
                const priorKey = `${rowData._id}-${runGroup.name}`

                const renderCaseRow = (workflowRunGroup: any, isPrior = false) => (
                  <div
                    className={`grid grid-cols-11 pb-4 ${isPrior ? 'opacity-60' : ''}`}
                    key={workflowRunGroup.systemRunId || workflowRunGroup.name}>
                    <div className="col-span-3 flex items-start gap-2">
                      <span>
                        <span className="font-semibold">Case:</span> {workflowRunGroup.name}
                      </span>
                      {isPrior && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                          Initial Run
                        </span>
                      )}
                    </div>
                    <div className="col-span-8 grid grid-cols-8">
                      {workflowRunGroup?.workflow.map((workflow, wfIndex) => {
                        return (
                          <div className="col-span-8 grid grid-cols-8" key={wfIndex}>
                            <div className="col-span-6 grid grid-cols-6">
                              <span className="flex col-span-6 font-semibold">
                                Workflow #{wfIndex + 1}
                              </span>
                              <span className="flex col-span-6">{workflow?.name}</span>
                            </div>
                            <div className="pl-8 ml-1 col-span-2 flex">
                              <SubmissionStatusCell status={workflow.overallStatus} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )

                return (
                  <div className="grid grid-cols-12" key={runGroup.name}>
                    <div className="ml-6 col-span-12">
                      {activeCases.map(wrg => renderCaseRow(wrg, false))}
                      {priorCases.length > 0 && (
                        <div className="mt-1 mb-2">
                          <div
                            className="flex items-center gap-2 cursor-pointer select-none py-1"
                            onClick={() =>
                              setOpenPriorSections(prev => ({
                                ...prev,
                                [priorKey]: !prev[priorKey]
                              }))
                            }>
                            {openPriorSections[priorKey] ? (
                              <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-4 w-4" />
                            ) : (
                              <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-4 w-4" />
                            )}
                            <span className="text-xs font-semibold text-gray-500">
                              Prior Cases ({priorCases.length})
                            </span>
                          </div>
                          {openPriorSections[priorKey] &&
                            priorCases.map(wrg => renderCaseRow(wrg, true))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </section>
          )
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={evt => {
          setSortField(evt.sortField)
          setSortOrder(evt.sortOrder)
        }}
        pt={{
          root: {
            className: 'mt-4'
          },
          bodyRow: {
            className: 'table-row'
          }
        }}>
        {columns.map(item => {
          return (
            <Column
              key={item.field}
              field={item.field}
              header={item.header}
              body={item.body}
              sortable={item.sortable}
              filterElement={item.filterElement}
              filter={item.filter}
              filterMatchMode="contains"
              filterFunction={item.filterFunction}
              filterField={item.field}
              showFilterMenu={false}
              headerClassName={item.headerClassName}
              bodyClassName={item.bodyClassName}></Column>
          )
        })}
        <Column
          rowEditor
          body={(rowData, options) => {
            return (
              <div className="flex items-center space-x-2">
                {rowData.overallStatus === RunStatus.CREATED && (
                  <IconButton
                    title="Submit"
                    severity="secondary"
                    loading={activeRunId === rowData._id && committingSubmission}
                    onClick={() => handleSubmitRun(rowData._id)}
                    icon={<PlayIcon className="fill-white" />}
                  />
                )}
                <IconButton
                  title="Compare"
                  severity="info"
                  onClick={() => {}}
                  icon={<CompareIcon className="fill-white" />}
                />
              </div>
            )
          }}
          headerClassName="editor-header"
          headerStyle={{ width: '100px' }}
          pt={{
            root: {
              className: 'px-2 py-2 text-center'
            }
          }}></Column>
      </DataTable>
    </>
  )
}

export default SubmissionList
